/**
 * Affinity reschedule customizations.
 *
 * 1) Keep Send now hidden and expand remaining actions equally.
 * 2) Intercept the default reschedule modal.
 * 3) Present delivery-date semantics:
 *    - Delay 1 week / 2 weeks (existing behavior)
 *    - Choose a specific delivery date (charge date = delivery date - 2 days)
 */
(function bootstrapAffinityReschedule() {
  if (window.__ET_AFFINITY_RESCHEDULE_LOADED__) return;
  window.__ET_AFFINITY_RESCHEDULE_LOADED__ = true;

  function resolvePortalTimeZone() {
    const configuredTimeZone = String(
      window.etAffinityRescheduleConfig?.timeZone || ''
    ).trim();
    if (configuredTimeZone) return configuredTimeZone;

    return 'America/Los_Angeles';
  }

  const CONFIG = Object.freeze({
    leadDays: 2,
    maxDaysInFuture: 14,
    overlayId: 'et-affinity-reschedule-overlay',
    timeZone: resolvePortalTimeZone(),
  });

  const state = {
    activeOverlay: null,
    openingOverlay: null,
    isSaving: false,
    sessionPromise: null,
  };

  installEventListeners();

  function installEventListeners() {
    document.addEventListener('Recharge::click::reschedule', handleRescheduleClick);
    document.addEventListener('click', handleRescheduleLinkClick, true);
  }

  function handleRescheduleLinkClick(event) {
    const target = event?.target instanceof Element ? event.target : null;
    if (!target) return;

    const rescheduleTrigger = target.closest('[data-action="et-reschedule"]');
    if (!(rescheduleTrigger instanceof HTMLElement)) return;

    if (
      rescheduleTrigger.getAttribute('aria-disabled') === 'true' ||
      rescheduleTrigger.getAttribute('data-et-disabled') === 'true' ||
      rescheduleTrigger.disabled ||
      rescheduleTrigger.hasAttribute('disabled')
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const subscriptionId = parseOptionalInteger(
      rescheduleTrigger.getAttribute('data-subscription-id')
    ) || parseOptionalInteger(window.etUpdateMealsOverviewSubscriptionId);

    if (!subscriptionId) {
      if (typeof window.etShowInlineErrorToast === 'function') {
        window.etShowInlineErrorToast('Subscription is still loading. Please try again.');
      }
      return;
    }

    rescheduleTrigger.closest('details')?.removeAttribute('open');
    document.dispatchEvent(
      new CustomEvent('Recharge::click::reschedule', {
        bubbles: true,
        cancelable: true,
        detail: { subscriptionIds: [subscriptionId], subscriptionId },
      })
    );
  }

  async function handleRescheduleClick(event) {
    // Override the native Affinity modal so all reschedule behavior is centralized here.
    if (typeof event?.preventDefault === 'function') {
      event.preventDefault();
    }

    const rescheduleContext = resolveRescheduleEventContext(event);

    if (!rescheduleContext.subscriptionIds.length) {
      console.warn(
        '[affinity-reschedule] Skipping custom reschedule modal because no subscription context was found.',
        {
          rawSubscriptionIds: rescheduleContext.rawSubscriptionIds,
        }
      );
      showRescheduleErrorToast('Subscription is still loading. Please try again.');
      reportRescheduleFailure(
        new Error('Reschedule was clicked without a resolvable subscription context.'),
        {
          issue: 'missing-subscription-context',
          rawSubscriptionIds: rescheduleContext.rawSubscriptionIds,
        }
      );
      return;
    }

    showOpeningOverlay('Loading reschedule options...');
    try {
      await openCustomRescheduleModal({
        subscriptionIds: rescheduleContext.subscriptionIds,
      });
    } catch (error) {
      console.warn('[affinity-reschedule] Failed to open custom reschedule modal.', error);
      showRescheduleErrorToast(
        (error && typeof error.etUserMessage === 'string' && error.etUserMessage) ||
          "We couldn't open your reschedule options. Please try again or contact us."
      );
      reportRescheduleFailure(error, {
        issue: 'open-modal-failed',
        subscriptionIds: rescheduleContext.subscriptionIds,
      });
    } finally {
      hideOpeningOverlay();
    }
  }

  function showRescheduleErrorToast(message) {
    if (typeof window.etShowInlineErrorToast === 'function') {
      window.etShowInlineErrorToast(message);
    }
  }

  // Reschedule failures are intentionally non-fatal for the page, so they must
  // still be captured in Sentry to stay diagnosable.
  function reportRescheduleFailure(error, context) {
    const observability = window.etObservability;
    if (!observability || typeof observability.captureException !== 'function') {
      return;
    }

    const issue = String(context?.issue || 'reschedule-failed');
    // Pass the raw value: et-sentry types non-Errors and records what threw,
    // which `String(error)` collapsed to "[object Object]" (SHOPIFY-3HD).
    observability.captureException(error, {
      level: 'error',
      fallbackMessage: issue,
      tags: {
        surface: 'affinity-reschedule',
        component: 'reschedule-modal',
        issue,
      },
      extra: {
        subscriptionIds: Array.isArray(context?.subscriptionIds)
          ? context.subscriptionIds
          : [],
        rawSubscriptionIds: Array.isArray(context?.rawSubscriptionIds)
          ? context.rawSubscriptionIds
          : [],
        pathname: window.location.pathname || '',
      },
    });
  }

  async function openCustomRescheduleModal(options) {
    closeActiveModal();

    // Session + subscription scope is resolved once, then reused through the full modal flow.
    const session = await getPortalSession();
    const subscriptions = await resolveTargetSubscriptions(
      session,
      options?.subscriptionIds || []
    );
    if (!subscriptions.length) {
      throw new Error('No subscriptions available for rescheduling.');
    }

    const fulfillmentContext = await resolveFulfillmentContext(session, subscriptions);
    const fulfillmentNoun = resolveFulfillmentNounLabel(fulfillmentContext?.noun);
    const initialDeliveryTime = normalizeQuarterHourTime(
      fulfillmentContext?.deliveryTime
    );
    // Availability may be missing/intermittent. We degrade to unbounded dates instead of blocking.
    const baselineChargeDate = subscriptions[0].nextChargeDate;
    if (!(baselineChargeDate instanceof Date)) {
      throw new Error('Unable to resolve current charge date.');
    }
    const baselineDeliveryDate = addDays(baselineChargeDate, CONFIG.leadDays);
    if (!(baselineDeliveryDate instanceof Date)) {
      throw new Error('Unable to resolve current delivery date.');
    }
    const dateWindow = getDeliveryDateWindowFromSubscriptionBaseline(
      baselineDeliveryDate
    );
    const availability = await loadFulfillmentAvailability(
      fulfillmentContext?.locationId,
      dateWindow.maxDeliveryDate
    );
    const minDeliveryDate = dateWindow.minDeliveryDate;
    const maxDeliveryDate = dateWindow.maxDeliveryDate;
    const context = {
      session,
      subscriptions,
      minDeliveryDate,
      maxDeliveryDate,
      baselineDeliveryDate,
      fulfillmentNoun,
      isDelivery: fulfillmentNoun === 'delivery',
      isPickup: fulfillmentNoun === 'pickup',
      availableDateSet: availability.availableDateSet,
      availableDateDetailsByDate: availability.availableDateDetailsByDate,
    };
    // Default specific-date selection to the next selectable date at/after baseline+lead.
    const initialSpecificDeliveryDate = findNextSelectableDate(
      context,
      maxDate(
        baselineDeliveryDate,
        minDeliveryDate
      )
    ) || minDeliveryDate;

    const overlay = document.createElement('div');
    overlay.id = CONFIG.overlayId;
    overlay.innerHTML = `
      <div class="et-affinity-reschedule-modal" role="dialog" aria-modal="true" aria-label="Reschedule delivery">
        <div class="et-affinity-reschedule-modal__header">
          <h2 class="et-affinity-reschedule-modal__title">When should your <span data-role="fulfillment-noun">delivery</span> arrive?</h2>
          <button type="button" class="et-affinity-reschedule-modal__close" data-role="close" aria-label="Close">&times;</button>
        </div>
        <div class="et-affinity-reschedule-modal__body">
          <fieldset class="et-affinity-reschedule-options">
            <div class="et-affinity-reschedule-option">
              <label class="et-affinity-reschedule-option__label">
                <input type="radio" name="et-affinity-reschedule-mode" value="delay_1_week" checked />
                Delay for 1 week
              </label>
            </div>
            <div class="et-affinity-reschedule-option">
              <label class="et-affinity-reschedule-option__label">
                <input type="radio" name="et-affinity-reschedule-mode" value="specific_delivery_date" />
                Choose a specific <span data-role="fulfillment-noun">delivery</span> date
              </label>
              <div class="et-affinity-reschedule-calendar" data-role="calendar" hidden>
                <div class="et-affinity-reschedule-calendar__nav">
                  <button type="button" class="et-affinity-reschedule-calendar__nav-btn" data-role="calendar-prev" aria-label="Previous month">&lsaquo;</button>
                  <h3 class="et-affinity-reschedule-calendar__title" data-role="calendar-month"></h3>
                  <button type="button" class="et-affinity-reschedule-calendar__nav-btn" data-role="calendar-next" aria-label="Next month">&rsaquo;</button>
                </div>
                <div class="et-affinity-reschedule-calendar__weekdays" aria-hidden="true">
                  <span class="et-affinity-reschedule-calendar__weekday">S</span>
                  <span class="et-affinity-reschedule-calendar__weekday">M</span>
                  <span class="et-affinity-reschedule-calendar__weekday">T</span>
                  <span class="et-affinity-reschedule-calendar__weekday">W</span>
                  <span class="et-affinity-reschedule-calendar__weekday">T</span>
                  <span class="et-affinity-reschedule-calendar__weekday">F</span>
                  <span class="et-affinity-reschedule-calendar__weekday">S</span>
                </div>
                <div class="et-affinity-reschedule-calendar__grid" data-role="calendar-grid"></div>
                <p class="et-affinity-reschedule-calendar__dow-message" data-role="calendar-dow-message" aria-live="polite" hidden></p>
              </div>
            </div>
          </fieldset>
          <div class="et-affinity-reschedule-error" data-role="error" aria-live="polite"></div>
          <div class="et-affinity-reschedule-modal__actions">
            <button type="button" class="et-affinity-reschedule-button et-affinity-reschedule-button--ghost" data-role="cancel">Cancel</button>
            <button type="button" class="et-affinity-reschedule-button et-affinity-reschedule-button--primary" data-role="save" disabled>Save changes</button>
          </div>
        </div>
        <div class="et-affinity-reschedule-saving" data-role="saving" hidden>
          <div class="et-affinity-reschedule-saving__content" aria-live="polite">
            <span class="et-affinity-reschedule-saving__spinner" aria-hidden="true"></span>
            <span data-role="saving-message">Saving changes...</span>
          </div>
        </div>
      </div>
    `;

    const refs = resolveModalRefs(overlay);
    if (!refs) {
      closeActiveModal();
      return;
    }

    applyFulfillmentNounCopy(refs, fulfillmentNoun);

    document.body.appendChild(overlay);
    state.activeOverlay = overlay;

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay && !refs.closeButton.disabled) {
        closeActiveModal();
      }
    });

    refs.closeButton.addEventListener('click', () => closeActiveModal());
    refs.cancelButton.addEventListener('click', () => closeActiveModal());

    const defaultModeInput = refs.formRoot.querySelector(
      'input[name="et-affinity-reschedule-mode"][value="delay_1_week"]'
    );
    if (defaultModeInput instanceof HTMLInputElement) {
      defaultModeInput.checked = true;
    }

    let selectedSpecificDeliveryDate = initialSpecificDeliveryDate;
    let calendarMonthDate = startOfMonth(initialSpecificDeliveryDate);
    setModalControlsDisabled(refs, false);
    setError(refs, '');
    setSavingState(refs, false);

    const renderCalendarGrid = () => {
      if (!(context.minDeliveryDate instanceof Date)) return;

      if (!(calendarMonthDate instanceof Date)) {
        calendarMonthDate = startOfMonth(
          selectedSpecificDeliveryDate || context.minDeliveryDate
        );
      }

      const monthStart = startOfMonth(calendarMonthDate);
      const previousMonthEnd = endOfMonth(addMonths(monthStart, -1));
      const canGoPrevious = !isBeforeDay(previousMonthEnd, context.minDeliveryDate);
      refs.calendarPrevButton.disabled = !canGoPrevious;
      
      const nextMonthStart = startOfMonth(addMonths(monthStart, 1));
      const canGoNext = !(context?.maxDeliveryDate instanceof Date) || !isAfterDay(nextMonthStart, context.maxDeliveryDate);
      refs.calendarNextButton.disabled = !canGoNext;
      
      refs.calendarMonth.textContent = formatMonthYear(monthStart);

      const year = monthStart.getFullYear();
      const monthIndex = monthStart.getMonth();
      const firstWeekday = monthStart.getDay();
      const daysInMonth = getDaysInMonth(year, monthIndex);

      refs.calendarGrid.innerHTML = '';
      // Fixed 6x7 grid mirrors month calendars and keeps layout stable across months.
      for (let cellIndex = 0; cellIndex < 42; cellIndex += 1) {
        const dayNumber = cellIndex - firstWeekday + 1;
        const dayButton = document.createElement('button');
        dayButton.type = 'button';
        dayButton.className = 'et-affinity-reschedule-calendar__day';

        if (dayNumber < 1 || dayNumber > daysInMonth) {
          dayButton.classList.add('et-affinity-reschedule-calendar__day--outside');
          dayButton.disabled = true;
          dayButton.tabIndex = -1;
          dayButton.textContent = '';
          refs.calendarGrid.appendChild(dayButton);
          continue;
        }

        const dateValue = new Date(year, monthIndex, dayNumber);
        const isoDateValue = formatDateInputValue(dateValue);
        dayButton.dataset.date = isoDateValue;
        dayButton.textContent = String(dayNumber);
        dayButton.setAttribute('aria-label', formatDisplayDate(dateValue));

        const isDisabled = !isCalendarDateSelectable(context, dateValue);
        if (isDisabled) {
          dayButton.disabled = true;
        }

        if (isSameDay(dateValue, selectedSpecificDeliveryDate) && !isDisabled) {
          dayButton.classList.add('et-affinity-reschedule-calendar__day--selected');
          dayButton.setAttribute('aria-pressed', 'true');
        } else {
          dayButton.setAttribute('aria-pressed', 'false');
        }

        refs.calendarGrid.appendChild(dayButton);
      }
    };

    const syncFormState = () => {
      const mode = getSelectedMode(refs.formRoot);
      const showCalendar = mode === 'specific_delivery_date';
      refs.calendar.hidden = !showCalendar;

      if (showCalendar) {
        // If selected date becomes invalid (API windows changed), snap to next valid option.
        if (
          !(selectedSpecificDeliveryDate instanceof Date) ||
          !isCalendarDateSelectable(context, selectedSpecificDeliveryDate)
        ) {
          selectedSpecificDeliveryDate =
            findNextSelectableDate(context, context.minDeliveryDate) ||
            context.minDeliveryDate;
        }
        if (
          !(calendarMonthDate instanceof Date) ||
          calendarMonthDate.getMonth() !== selectedSpecificDeliveryDate.getMonth() ||
          calendarMonthDate.getFullYear() !==
            selectedSpecificDeliveryDate.getFullYear()
        ) {
          calendarMonthDate = startOfMonth(selectedSpecificDeliveryDate);
        }
        renderCalendarGrid();
        // Show day-of-week change message when selected date is a different weekday than current subscription.
        const baseline = context.baselineDeliveryDate;
        if (
          refs.calendarDowMessage &&
          selectedSpecificDeliveryDate instanceof Date &&
          baseline instanceof Date
        ) {
          const oldDow = baseline.getDay();
          const newDow = selectedSpecificDeliveryDate.getDay();
          if (oldDow !== newDow) {
            refs.calendarDowMessage.textContent =
              'Subscription was on ' +
              formatDayOfWeek(baseline) +
              ' and is now on ' +
              formatDayOfWeek(selectedSpecificDeliveryDate) +
              '.';
            refs.calendarDowMessage.hidden = false;
          } else {
            refs.calendarDowMessage.textContent = '';
            refs.calendarDowMessage.hidden = true;
          }
        } else if (refs.calendarDowMessage) {
          refs.calendarDowMessage.textContent = '';
          refs.calendarDowMessage.hidden = true;
        }
      }
    };

    refs.formRoot.addEventListener('change', syncFormState);
    refs.calendarPrevButton.addEventListener('click', () => {
      const candidate = addMonths(calendarMonthDate, -1);
      if (!candidate) return;
      const candidateEnd = endOfMonth(candidate);
      if (isBeforeDay(candidateEnd, context.minDeliveryDate)) return;
      calendarMonthDate = candidate;
      renderCalendarGrid();
    });
    refs.calendarNextButton.addEventListener('click', () => {
      const candidate = addMonths(calendarMonthDate, 1);
      if (!candidate) return;
      const candidateStart = startOfMonth(candidate);
      if (context?.maxDeliveryDate instanceof Date && isAfterDay(candidateStart, context.maxDeliveryDate)) return;
      calendarMonthDate = candidate;
      renderCalendarGrid();
    });
    refs.calendarGrid.addEventListener('click', (event) => {
      const targetButton = event.target;
      if (!(targetButton instanceof HTMLElement)) return;
      const dateButton = targetButton.closest('button[data-date]');
      if (!(dateButton instanceof HTMLButtonElement) || dateButton.disabled) return;
      const parsed = parseDateValue(dateButton.dataset.date);
      if (!(parsed instanceof Date)) return;
      selectedSpecificDeliveryDate = parsed;
      renderCalendarGrid();
      syncFormState();
    });
    syncFormState();

    refs.saveButton.addEventListener('click', async () => {
      if (state.isSaving) return;

      const mode = getSelectedMode(refs.formRoot);
      const specificDeliveryDate = selectedSpecificDeliveryDate;
      let specificDeliveryTime = '';

      if (mode === 'specific_delivery_date') {
        const validationError = validateSpecificDateSelection(
          context,
          specificDeliveryDate
        );
        if (validationError) {
          setError(refs, validationError);
          return;
        }
      }

      setModalControlsDisabled(refs, true);
      setError(refs, '');

      if (context.isDelivery) {
        const targetDeliveryDate = resolveTargetDeliveryDateForMode({
          mode,
          baselineChargeDate,
          specificDeliveryDate,
        });
        // Time bounds are date-specific and come from availability for the chosen target date.
        const timeBounds = resolveTimeBoundsForDate(context, targetDeliveryDate);
        const confirmedTime = await openDeliveryTimeConfirmModal({
          initialDeliveryTime,
          minTime: timeBounds?.minTime || '',
          maxTime: timeBounds?.maxTime || '',
          targetDeliveryDate,
        });
        if (!confirmedTime) {
          setModalControlsDisabled(refs, false);
          return;
        }
        specificDeliveryTime = confirmedTime;
      }

      setSavingState(refs, true);
      state.isSaving = true;

      try {
        const updatedSubscriptionIds = await persistReschedule({
          session: context.session,
          subscriptions: context.subscriptions,
          mode,
          specificDeliveryDate,
          specificDeliveryTime,
          fulfillmentNoun: context.fulfillmentNoun,
        });

        emitRescheduleEvents(updatedSubscriptionIds);
        closeActiveModal();
      } catch (error) {
        const fallbackMessage = 'Unable to save reschedule changes.';
        const message =
          error instanceof Error && error.message ? error.message : fallbackMessage;
        setError(refs, message);
        setModalControlsDisabled(refs, false);
      } finally {
        if (state.activeOverlay === overlay) {
          setSavingState(refs, false);
        }
        state.isSaving = false;
      }
    });
  }

  function resolveModalRefs(overlay) {
    const formRoot = overlay.querySelector('.et-affinity-reschedule-modal');
    const title = overlay.querySelector('.et-affinity-reschedule-modal__title');
    const fulfillmentNounNodes = Array.from(
      overlay.querySelectorAll('[data-role="fulfillment-noun"]')
    );
    const error = overlay.querySelector('[data-role="error"]');
    const calendar = overlay.querySelector('[data-role="calendar"]');
    const calendarPrevButton = overlay.querySelector('[data-role="calendar-prev"]');
    const calendarNextButton = overlay.querySelector('[data-role="calendar-next"]');
    const calendarMonth = overlay.querySelector('[data-role="calendar-month"]');
    const calendarGrid = overlay.querySelector('[data-role="calendar-grid"]');
    const calendarDowMessage = overlay.querySelector('[data-role="calendar-dow-message"]');
    const savingOverlay = overlay.querySelector('[data-role="saving"]');
    const savingMessage = overlay.querySelector('[data-role="saving-message"]');
    const saveButton = overlay.querySelector('[data-role="save"]');
    const cancelButton = overlay.querySelector('[data-role="cancel"]');
    const closeButton = overlay.querySelector('[data-role="close"]');

    if (
      !(formRoot instanceof HTMLElement) ||
      !(title instanceof HTMLElement) ||
      !fulfillmentNounNodes.length ||
      !(error instanceof HTMLElement) ||
      !(calendar instanceof HTMLElement) ||
      !(calendarPrevButton instanceof HTMLButtonElement) ||
      !(calendarNextButton instanceof HTMLButtonElement) ||
      !(calendarMonth instanceof HTMLElement) ||
      !(calendarGrid instanceof HTMLElement) ||
      !(calendarDowMessage instanceof HTMLElement) ||
      !(savingOverlay instanceof HTMLElement) ||
      !(savingMessage instanceof HTMLElement) ||
      !(saveButton instanceof HTMLButtonElement) ||
      !(cancelButton instanceof HTMLButtonElement) ||
      !(closeButton instanceof HTMLButtonElement)
    ) {
      return null;
    }

    return {
      formRoot,
      title,
      fulfillmentNounNodes,
      error,
      calendar,
      calendarPrevButton,
      calendarNextButton,
      calendarMonth,
      calendarGrid,
      calendarDowMessage,
      savingOverlay,
      savingMessage,
      saveButton,
      cancelButton,
      closeButton,
    };
  }

  function closeActiveModal() {
    if (state.activeOverlay && state.activeOverlay.parentNode) {
      state.activeOverlay.parentNode.removeChild(state.activeOverlay);
    }
    state.activeOverlay = null;
    state.isSaving = false;
  }

  function showOpeningOverlay(message) {
    hideOpeningOverlay();

    const overlay = document.createElement('div');
    overlay.className = 'et-affinity-reschedule-opening';
    overlay.innerHTML = `
      <div class="et-affinity-reschedule-opening__content" role="status" aria-live="polite">
        <span class="et-affinity-reschedule-saving__spinner" aria-hidden="true"></span>
        <span>${String(message || 'Loading...')}</span>
      </div>
    `;

    document.body.appendChild(overlay);
    state.openingOverlay = overlay;
  }

  function hideOpeningOverlay() {
    if (state.openingOverlay && state.openingOverlay.parentNode) {
      state.openingOverlay.parentNode.removeChild(state.openingOverlay);
    }
    state.openingOverlay = null;
  }

  function setError(refs, message) {
    refs.error.textContent = String(message || '').trim();
  }

  function setModalControlsDisabled(refs, disabled) {
    const isDisabled = Boolean(disabled);
    refs.saveButton.disabled = isDisabled;
    refs.cancelButton.disabled = isDisabled;
    refs.closeButton.disabled = isDisabled;
  }

  function validateSpecificDateSelection(context, specificDeliveryDate) {
    const fulfillmentDateLabel = formatFulfillmentDateLabel(context?.fulfillmentNoun);
    if (!(specificDeliveryDate instanceof Date)) {
      return `Select a valid ${fulfillmentDateLabel.toLowerCase()}.`;
    }

    if (specificDeliveryDate < context.minDeliveryDate) {
      return `${fulfillmentDateLabel} must be on or after ${formatDisplayDate(
        context.minDeliveryDate
      )}.`;
    }

    if (context?.maxDeliveryDate instanceof Date && isAfterDay(specificDeliveryDate, context.maxDeliveryDate)) {
      return `${fulfillmentDateLabel} must be on or before ${formatDisplayDate(
        context.maxDeliveryDate
      )}.`;
    }

    if (!isCalendarDateSelectable(context, specificDeliveryDate)) {
      return `Select an available ${fulfillmentDateLabel.toLowerCase()}.`;
    }

    return '';
  }

  function setSavingState(refs, isSaving, message) {
    const saving = Boolean(isSaving);
    if (refs?.savingOverlay instanceof HTMLElement) {
      refs.savingOverlay.hidden = !saving;
    }
    if (refs?.savingMessage instanceof HTMLElement) {
      refs.savingMessage.textContent = saving
        ? String(message || 'Saving changes...')
        : 'Saving changes...';
    }
    if (refs?.formRoot instanceof HTMLElement) {
      refs.formRoot.setAttribute('aria-busy', saving ? 'true' : 'false');
    }
  }

  function openDeliveryTimeConfirmModal(options) {
    const initialDeliveryTime = normalizeQuarterHourTime(options?.initialDeliveryTime);
    const minTime = normalizeTimeInputValue(options?.minTime);
    const maxTime = normalizeTimeInputValue(options?.maxTime);
    const targetDeliveryDate =
      options?.targetDeliveryDate instanceof Date ? options.targetDeliveryDate : null;

    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'et-affinity-reschedule-time-overlay';
      overlay.id = `${CONFIG.overlayId}-time-confirm`;
      overlay.innerHTML = `
        <div class="et-affinity-reschedule-modal" role="dialog" aria-modal="true" aria-label="Reconfirm delivery time">
          <div class="et-affinity-reschedule-modal__header">
            <h2 class="et-affinity-reschedule-modal__title">Reconfirm delivery time</h2>
            <button type="button" class="et-affinity-reschedule-modal__close" data-role="close" aria-label="Close">&times;</button>
          </div>
          <div class="et-affinity-reschedule-modal__body">
            <div class="et-affinity-reschedule-time">
              <label class="et-affinity-reschedule-time__label" for="et-affinity-reschedule-time-confirm-input">
                Delivery time
              </label>
              <p class="et-affinity-reschedule-time__hint" data-role="time-window"></p>
              <select
                class="et-affinity-reschedule-time__input"
                id="et-affinity-reschedule-time-confirm-input"
                data-role="time-input"
              ></select>
            </div>
            <div class="et-affinity-reschedule-error" data-role="error" aria-live="polite"></div>
            <div class="et-affinity-reschedule-modal__actions">
              <button type="button" class="et-affinity-reschedule-button et-affinity-reschedule-button--ghost" data-role="cancel">Cancel</button>
              <button type="button" class="et-affinity-reschedule-button et-affinity-reschedule-button--primary" data-role="save">Save changes</button>
            </div>
          </div>
        </div>
      `;

      const closeButton = overlay.querySelector('[data-role="close"]');
      const cancelButton = overlay.querySelector('[data-role="cancel"]');
      const saveButton = overlay.querySelector('[data-role="save"]');
      const errorNode = overlay.querySelector('[data-role="error"]');
      const timeInput = overlay.querySelector('[data-role="time-input"]');
      const timeWindow = overlay.querySelector('[data-role="time-window"]');

      if (
        !(closeButton instanceof HTMLButtonElement) ||
        !(cancelButton instanceof HTMLButtonElement) ||
        !(saveButton instanceof HTMLButtonElement) ||
        !(errorNode instanceof HTMLElement) ||
        !(timeInput instanceof HTMLSelectElement) ||
        !(timeWindow instanceof HTMLElement)
      ) {
        resolve('');
        return;
      }

      const teardown = (value) => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
        resolve(value);
      };

      const setModalError = (message) => {
        errorNode.textContent = String(message || '').trim();
      };

      const hasTimeOptions = populateQuarterHourTimeOptions(timeInput, initialDeliveryTime, {
        minTime,
        maxTime,
      });
      // No valid slots means Save remains disabled and user gets a deterministic message.
      saveButton.disabled = !hasTimeOptions;
      const timeWindowHint = formatTimeWindowHint({
        dateValue: targetDeliveryDate,
        minTime,
        maxTime,
      });
      timeWindow.textContent = timeWindowHint;
      timeWindow.hidden = !timeWindowHint;
      if (!hasTimeOptions) {
        setModalError('No available times for this date.');
      }

      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
          teardown('');
        }
      });
      closeButton.addEventListener('click', () => teardown(''));
      cancelButton.addEventListener('click', () => teardown(''));
      timeInput.addEventListener('input', () => {
        setModalError('');
      });
      saveButton.addEventListener('click', () => {
        const normalized = normalizeTimeInputValue(timeInput.value);
        if (!normalized) {
          setModalError('Choose a valid delivery time.');
          timeInput.focus();
          return;
        }

        if (!isQuarterHourTime(normalized)) {
          setModalError('Choose a time in 15-minute increments.');
          timeInput.focus();
          return;
        }

        if (!isTimeWithinBounds(normalized, minTime, maxTime)) {
          setModalError('Choose a time within the available window.');
          timeInput.focus();
          return;
        }

        teardown(normalizeQuarterHourTime(normalized));
      });

      document.body.appendChild(overlay);
      timeInput.focus();
    });
  }

  function applyFulfillmentNounCopy(refs, fulfillmentNoun) {
    const resolvedNoun = resolveFulfillmentNounLabel(fulfillmentNoun);
    const headingText =
      resolvedNoun === 'pickup'
        ? 'When would you like your next pickup?'
        : 'When should your delivery arrive?';

    if (refs?.title instanceof HTMLElement) {
      refs.title.textContent = headingText;
    }

    const nodes = Array.isArray(refs?.fulfillmentNounNodes)
      ? refs.fulfillmentNounNodes
      : [];
    for (const node of nodes) {
      if (node instanceof HTMLElement) {
        node.textContent = resolvedNoun;
      }
    }
    if (refs?.formRoot instanceof HTMLElement) {
      refs.formRoot.setAttribute('aria-label', `Reschedule ${resolvedNoun}`);
    }
  }

  function formatFulfillmentDateLabel(fulfillmentNoun) {
    const resolvedNoun = resolveFulfillmentNounLabel(fulfillmentNoun);
    return `${resolvedNoun.charAt(0).toUpperCase()}${resolvedNoun.slice(1)} date`;
  }

  function resolveFulfillmentNounLabel(rawValue) {
    const normalized = normalizeAttributeKey(rawValue);
    if (normalized.includes('pickup')) return 'pickup';
    return 'delivery';
  }

  async function resolveFulfillmentContext(session, subscriptions) {
    const fallback = {
      noun: 'delivery',
      deliveryTime: '',
      locationId: '',
    };
    if (!session || !Array.isArray(subscriptions) || !subscriptions.length) {
      return fallback;
    }

    const orderedAddressIds = [];
    const primaryAddressId = parseOptionalInteger(subscriptions[0]?.addressId);
    if (primaryAddressId) {
      orderedAddressIds.push(primaryAddressId);
    }

    const addressIds = normalizeIntegerList(
      subscriptions.map((entry) => parseOptionalInteger(entry?.addressId))
    );
    for (const addressId of addressIds) {
      if (orderedAddressIds.includes(addressId)) continue;
      orderedAddressIds.push(addressId);
    }
    if (!orderedAddressIds.length) return fallback;

    for (const addressId of orderedAddressIds) {
      try {
        const addressResponse = await getAddress(session, addressId);
        const address = normalizeAddressResponse(addressResponse);
        if (!address || typeof address !== 'object') continue;

        const attributes = normalizeOrderAttributes(
          address.order_attributes ?? address.orderAttributes ?? address.attributes
        );
        const fulfillmentTypeValue =
          readNamedAttributeValue(attributes, 'Fulfillment Type') || '';
        const fulfillmentDateValue =
          readNamedAttributeValue(attributes, 'Fulfillment Date') || '';
        const locationIdValue =
          readNamedAttributeValue(attributes, 'LocationId') || '';
        return {
          noun: resolveFulfillmentNounLabel(fulfillmentTypeValue),
          deliveryTime: extractTimeInputValueFromFulfillmentDate(fulfillmentDateValue),
          locationId: normalizeLocationId(locationIdValue),
        };
      } catch (_error) {
        // Fallback to delivery defaults when address metadata cannot be resolved.
      }
    }

    return fallback;
  }

  function normalizeTimeInputValue(rawValue) {
    const parsed = parseTimeComponents(rawValue);
    if (!parsed) return '';
    const hours = String(parsed.hours).padStart(2, '0');
    const minutes = String(parsed.minutes).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  function normalizeQuarterHourTime(rawValue) {
    const normalized = normalizeTimeInputValue(rawValue);
    if (!normalized) return '';
    const parsed = parseTimeComponents(normalized);
    if (!parsed) return '';

    const totalMinutes = parsed.hours * 60 + parsed.minutes;
    let roundedMinutes = Math.round(totalMinutes / 15) * 15;
    if (roundedMinutes >= 24 * 60) {
      roundedMinutes = 24 * 60 - 15;
    }
    if (roundedMinutes < 0) {
      roundedMinutes = 0;
    }

    const hours = Math.floor(roundedMinutes / 60);
    const minutes = roundedMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  function populateQuarterHourTimeOptions(selectElement, selectedTime, bounds) {
    if (!(selectElement instanceof HTMLSelectElement)) return false;

    const normalizedSelected = normalizeQuarterHourTime(selectedTime);
    const normalizedMinTime = normalizeTimeInputValue(bounds?.minTime);
    const normalizedMaxTime = normalizeTimeInputValue(bounds?.maxTime);
    const timeRange = resolveQuarterHourRange(normalizedMinTime, normalizedMaxTime);
    const options = ['<option value="">Select time</option>'];
    if (timeRange.hasAvailableSlots) {
      for (
        let totalMinutes = timeRange.minMinutes;
        totalMinutes <= timeRange.maxMinutes;
        totalMinutes += 15
      ) {
        const value = formatTimeValueFromTotalMinutes(totalMinutes);
        const label = formatQuarterHourLabel(value);
        options.push(`<option value="${value}">${label}</option>`);
      }
    }

    selectElement.innerHTML = options.join('');
    if (!timeRange.hasAvailableSlots) {
      return false;
    }

    if (
      normalizedSelected &&
      isTimeWithinBounds(normalizedSelected, normalizedMinTime, normalizedMaxTime)
    ) {
      selectElement.value = normalizedSelected;
      return true;
    }

    const firstAvailableOption = selectElement.querySelector(
      'option[value]:not([value=""])'
    );
    if (firstAvailableOption instanceof HTMLOptionElement) {
      selectElement.value = firstAvailableOption.value;
    }
    return true;
  }

  function resolveQuarterHourRange(minTime, maxTime) {
    const defaultMin = 0;
    const defaultMax = 24 * 60 - 15;

    const parsedMin = parseTimeComponents(minTime);
    const parsedMax = parseTimeComponents(maxTime);

    const boundedMin = parsedMin
      ? Math.ceil((parsedMin.hours * 60 + parsedMin.minutes) / 15) * 15
      : defaultMin;
    const boundedMax = parsedMax
      ? Math.floor((parsedMax.hours * 60 + parsedMax.minutes) / 15) * 15
      : defaultMax;

    const minMinutes = clampToQuarterHourBounds(boundedMin);
    const maxMinutes = clampToQuarterHourBounds(boundedMax);
    return {
      minMinutes,
      maxMinutes,
      // Do not fall back to all-day options when bounds are invalid.
      hasAvailableSlots: minMinutes <= maxMinutes,
    };
  }

  function clampToQuarterHourBounds(totalMinutes) {
    const parsed = Number.parseInt(String(totalMinutes), 10);
    if (!Number.isFinite(parsed)) return 0;
    if (parsed < 0) return 0;
    if (parsed > 24 * 60 - 15) return 24 * 60 - 15;
    return parsed;
  }

  function formatTimeValueFromTotalMinutes(totalMinutes) {
    const safeMinutes = clampToQuarterHourBounds(totalMinutes);
    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  function isTimeWithinBounds(timeValue, minTime, maxTime) {
    const normalizedTime = normalizeTimeInputValue(timeValue);
    if (!normalizedTime) return false;

    const parsedTime = parseTimeComponents(normalizedTime);
    if (!parsedTime) return false;

    const timeInMinutes = parsedTime.hours * 60 + parsedTime.minutes;
    const range = resolveQuarterHourRange(minTime, maxTime);
    if (!range.hasAvailableSlots) return false;
    return timeInMinutes >= range.minMinutes && timeInMinutes <= range.maxMinutes;
  }

  function formatTimeWindowHint(options) {
    const dateValue = options?.dateValue;
    const minTime = normalizeTimeInputValue(options?.minTime);
    const maxTime = normalizeTimeInputValue(options?.maxTime);
    if (!(dateValue instanceof Date) || !minTime || !maxTime) return '';

    return `${formatDisplayDate(dateValue)}: ${formatQuarterHourLabel(
      minTime
    )} - ${formatQuarterHourLabel(maxTime)}`;
  }

  function addMinutesToTime(rawTime, minutesToAdd) {
    const parsedTime = parseTimeComponents(rawTime);
    if (!parsedTime) return '';

    const parsedMinutesToAdd = Number.parseInt(String(minutesToAdd), 10);
    const safeMinutesToAdd = Number.isFinite(parsedMinutesToAdd)
      ? parsedMinutesToAdd
      : 0;
    const totalMinutes = parsedTime.hours * 60 + parsedTime.minutes + safeMinutesToAdd;
    const clampedMinutes = Math.max(0, Math.min(24 * 60 - 1, totalMinutes));
    const hours = Math.floor(clampedMinutes / 60);
    const minutes = clampedMinutes % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  function formatQuarterHourLabel(timeValue) {
    const parsed = parseTimeComponents(timeValue);
    if (!parsed) return String(timeValue || '');

    const hour24 = parsed.hours;
    const minute = String(parsed.minutes).padStart(2, '0');
    const isPm = hour24 >= 12;
    const hour12 = hour24 % 12 || 12;
    return `${hour12}:${minute} ${isPm ? 'PM' : 'AM'}`;
  }

  function isQuarterHourTime(rawValue) {
    const normalized = normalizeTimeInputValue(rawValue);
    if (!normalized) return false;
    const parsed = parseTimeComponents(normalized);
    if (!parsed) return false;
    return parsed.minutes % 15 === 0;
  }

  function extractTimeInputValueFromFulfillmentDate(rawValue) {
    return normalizeTimeInputValue(rawValue);
  }

  function parseTimeComponents(rawValue) {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;

    const directMatch = raw.match(
      /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/
    );
    if (directMatch) {
      return normalizeTimeParts(directMatch[1], directMatch[2], directMatch[3] || '');
    }

    const embeddedMeridiemMatch = raw.match(
      /(?:T|\s)(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])\b/
    );
    if (embeddedMeridiemMatch) {
      return normalizeTimeParts(
        embeddedMeridiemMatch[1],
        embeddedMeridiemMatch[2],
        embeddedMeridiemMatch[3]
      );
    }

    const embedded24hMatch = raw.match(/(?:T|\s)(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (embedded24hMatch) {
      return normalizeTimeParts(embedded24hMatch[1], embedded24hMatch[2], '');
    }

    return null;
  }

  function normalizeTimeParts(rawHours, rawMinutes, rawMeridiem) {
    const minutes = Number.parseInt(String(rawMinutes), 10);
    if (!Number.isFinite(minutes) || minutes < 0 || minutes > 59) {
      return null;
    }

    const hours = Number.parseInt(String(rawHours), 10);
    if (!Number.isFinite(hours)) {
      return null;
    }

    const meridiem = String(rawMeridiem || '').trim().toLowerCase();
    if (meridiem) {
      if (hours < 1 || hours > 12) {
        return null;
      }

      const normalizedHours = hours % 12;
      return {
        hours: meridiem === 'pm' ? normalizedHours + 12 : normalizedHours,
        minutes,
      };
    }

    if (hours < 0 || hours > 23) {
      return null;
    }

    return { hours, minutes };
  }

  function getSelectedMode(rootElement) {
    const checked = rootElement.querySelector(
      'input[name="et-affinity-reschedule-mode"]:checked'
    );
    const mode = String(checked?.value || 'delay_1_week');
    if (
      mode !== 'delay_1_week' &&
      mode !== 'specific_delivery_date'
    ) {
      return 'delay_1_week';
    }
    return mode;
  }

  async function persistReschedule(options) {
    const session = options?.session;
    const subscriptions = Array.isArray(options?.subscriptions)
      ? options.subscriptions
      : [];
    const mode = options?.mode || 'delay_1_week';
    const specificDeliveryDate = options?.specificDeliveryDate || null;
    const specificDeliveryTime = normalizeQuarterHourTime(
      options?.specificDeliveryTime
    );
    const fulfillmentNoun = resolveFulfillmentNounLabel(options?.fulfillmentNoun);
    const applyDeliveryTime =
      fulfillmentNoun === 'delivery' && Boolean(specificDeliveryTime);

    if (!session) throw new Error('Portal session is unavailable.');
    if (!subscriptions.length) throw new Error('No subscriptions available for reschedule.');

    const subscriptionUpdateCalls = [];
    const updatedSubscriptionIds = [];
    const addressUpdatesById = new Map();

    for (const entry of subscriptions) {
      if (!entry || !entry.id || !(entry.nextChargeDate instanceof Date)) continue;

      let nextChargeDate = entry.nextChargeDate;
      if (mode === 'delay_1_week') {
        nextChargeDate = addDays(entry.nextChargeDate, 7);
      } else if (mode === 'specific_delivery_date') {
        if (!(specificDeliveryDate instanceof Date)) {
          throw new Error('Select a valid date.');
        }
        nextChargeDate = addDays(specificDeliveryDate, -CONFIG.leadDays);
      }

      if (!(nextChargeDate instanceof Date)) {
        throw new Error('Unable to calculate next charge date.');
      }

      const nextChargeDateValue = formatDateInputValue(nextChargeDate);
      subscriptionUpdateCalls.push(
        updateSubscriptionChargeDate(session, entry.id, nextChargeDateValue)
      );
      updatedSubscriptionIds.push(entry.id);

      const addressId = parseOptionalInteger(entry.addressId);
      if (addressId) {
        const nextDeliveryDate = addDays(nextChargeDate, CONFIG.leadDays);
        if (nextDeliveryDate instanceof Date) {
          const existingDate = addressUpdatesById.get(addressId);
          if (
            !(existingDate instanceof Date) ||
            nextDeliveryDate.getTime() < existingDate.getTime()
          ) {
            addressUpdatesById.set(addressId, nextDeliveryDate);
          }
        }
      }
    }

    if (!subscriptionUpdateCalls.length) {
      throw new Error('No charge dates were updated.');
    }

    await Promise.all(subscriptionUpdateCalls);

    const addressUpdateCalls = [];
    for (const [addressId, deliveryDate] of addressUpdatesById.entries()) {
      addressUpdateCalls.push(
        updateAddressFulfillmentDate({
          session,
          addressId,
          deliveryDate,
          deliveryTime: applyDeliveryTime ? specificDeliveryTime : '',
        })
      );
    }

    if (addressUpdateCalls.length) {
      await Promise.all(addressUpdateCalls);
    }

    return updatedSubscriptionIds;
  }

  function emitRescheduleEvents(subscriptionIds) {
    const payload = {
      subscriptionIds: normalizeIntegerList(subscriptionIds),
    };
    const analytics = {
      pathname: window.location.pathname || '',
      timestamp: new Date().toISOString(),
    };

    document.dispatchEvent(
      new CustomEvent('Recharge::action::orderChanged', {
        detail: { payload, analytics },
      })
    );
    document.dispatchEvent(
      new CustomEvent('Recharge::action::reschedule', {
        detail: { payload, analytics },
      })
    );
    document.dispatchEvent(
      new CustomEvent('Affinity:refresh', {
        detail: { payload, analytics },
      })
    );
  }

  async function resolveTargetSubscriptions(session, requestedIds) {
    const response = await listSubscriptions(session, { limit: 250 });
    const subscriptions = normalizeSubscriptionList(response);
    if (!subscriptions.length) return [];

    const mapped = subscriptions.map((subscription) => {
      const id = parseOptionalInteger(subscription?.id);
      const nextChargeDate = extractNextChargeDate(subscription);
      const addressId = resolveSubscriptionAddressId(subscription);
      return {
        subscription,
        id,
        nextChargeDate,
        addressId,
      };
    });
    const normalized = mapped.filter(
      (entry) => entry.id && entry.nextChargeDate instanceof Date
    );

    const normalizedRequestedIds = normalizeIntegerList(requestedIds);
    if (normalizedRequestedIds.length) {
      const requestedSet = new Set(normalizedRequestedIds);
      const requestedMatches = normalized.filter((entry) =>
        requestedSet.has(entry.id)
      );
      if (!requestedMatches.length) {
        // The subscription exists but Recharge has no upcoming charge for it
        // (failed/errored charge, pause, or missing regeneration) — surface
        // that to the customer instead of a generic failure.
        const requestedExistsWithoutChargeDate = mapped.some(
          (entry) => entry.id && requestedSet.has(entry.id)
        );
        if (requestedExistsWithoutChargeDate) {
          const noChargeError = new Error(
            'Requested subscription has no upcoming charge date to reschedule.'
          );
          noChargeError.etUserMessage =
            "We couldn't find an upcoming order for this subscription, so it can't be rescheduled right now. Please contact us for help.";
          throw noChargeError;
        }
        throw new Error('Unable to resolve requested subscription context.');
      }
      return requestedMatches;
    }

    if (!normalized.length) return [];

    normalized.sort((left, right) => left.nextChargeDate - right.nextChargeDate);
    return [normalized[0]];
  }

  function normalizeSubscriptionList(response) {
    if (!response || typeof response !== 'object') return [];
    if (Array.isArray(response.subscriptions)) return response.subscriptions;
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.results)) return response.results;
    return [];
  }

  function extractNextChargeDate(subscription) {
    if (!subscription || typeof subscription !== 'object') return null;

    const dateCandidates = [
      subscription.next_charge_date,
      subscription.next_charge_scheduled_at,
      subscription.next_charge_at,
      subscription.nextChargeDate,
      subscription.nextChargeScheduledAt,
      subscription.nextChargeAt,
    ];

    for (const candidate of dateCandidates) {
      const parsed = parseDateValue(candidate);
      if (parsed) return parsed;
    }

    return null;
  }

  function resolveSubscriptionAddressId(subscription) {
    const sharedResolver = window.etRechargeUtils?.resolveSubscriptionAddressId;
    if (typeof sharedResolver === 'function') {
      return sharedResolver(subscription);
    }
    if (!subscription || typeof subscription !== 'object') return null;

    const candidates = [
      subscription.address_id,
      subscription.addressId,
      subscription.address?.id,
      subscription.address?.address_id,
      subscription.shipping_address_id,
      subscription.shippingAddressId,
    ];

    for (const candidate of candidates) {
      const parsed = parseOptionalInteger(candidate);
      if (parsed) return parsed;
    }

    return null;
  }

  function parseDateValue(rawValue) {
    const sharedParser = window.etRechargeUtils?.parseDateValue;
    if (typeof sharedParser === 'function') {
      return sharedParser(rawValue);
    }
    const raw = String(rawValue || '').trim();
    if (!raw) return null;

    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;

    const year = Number.parseInt(match[1], 10);
    const monthIndex = Number.parseInt(match[2], 10) - 1;
    const day = Number.parseInt(match[3], 10);
    if (
      !Number.isFinite(year) ||
      !Number.isFinite(monthIndex) ||
      !Number.isFinite(day)
    ) {
      return null;
    }

    const parsed = new Date(year, monthIndex, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== monthIndex ||
      parsed.getDate() !== day
    ) {
      return null;
    }

    return parsed;
  }

  function addDays(dateValue, daysToAdd) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return null;
    }

    const days = Number.parseInt(String(daysToAdd || 0), 10);
    const safeDays = Number.isFinite(days) ? days : 0;
    const result = new Date(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate()
    );
    result.setDate(result.getDate() + safeDays);
    return result;
  }

  function startOfDay(dateValue) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return null;
    }
    return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
  }

  function startOfMonth(dateValue) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return null;
    }
    return new Date(dateValue.getFullYear(), dateValue.getMonth(), 1);
  }

  function endOfMonth(dateValue) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return null;
    }
    return new Date(dateValue.getFullYear(), dateValue.getMonth() + 1, 0);
  }

  function addMonths(dateValue, monthOffset) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return null;
    }

    const offset = Number.parseInt(String(monthOffset || 0), 10);
    const safeOffset = Number.isFinite(offset) ? offset : 0;
    return new Date(dateValue.getFullYear(), dateValue.getMonth() + safeOffset, 1);
  }

  function getDaysInMonth(year, monthIndex) {
    if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return 0;
    return new Date(year, monthIndex + 1, 0).getDate();
  }

  function isBeforeDay(left, right) {
    const leftDay = startOfDay(left);
    const rightDay = startOfDay(right);
    if (!(leftDay instanceof Date) || !(rightDay instanceof Date)) return false;
    return leftDay.getTime() < rightDay.getTime();
  }

  function isAfterDay(left, right) {
    const leftDay = startOfDay(left);
    const rightDay = startOfDay(right);
    if (!(leftDay instanceof Date) || !(rightDay instanceof Date)) return false;
    return leftDay.getTime() > rightDay.getTime();
  }

  function isSameDay(left, right) {
    const leftDay = startOfDay(left);
    const rightDay = startOfDay(right);
    if (!(leftDay instanceof Date) || !(rightDay instanceof Date)) return false;
    return (
      leftDay.getFullYear() === rightDay.getFullYear() &&
      leftDay.getMonth() === rightDay.getMonth() &&
      leftDay.getDate() === rightDay.getDate()
    );
  }

  function formatMonthYear(dateValue) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(dateValue);
  }

  function formatDayOfWeek(dateValue) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(dateValue);
  }

  function maxDate(left, right) {
    if (!(left instanceof Date)) return right instanceof Date ? right : null;
    if (!(right instanceof Date)) return left;
    return left.getTime() >= right.getTime() ? left : right;
  }

  function getDeliveryDateWindowFromSubscriptionBaseline(baselineDeliveryDate) {
    const baseline = baselineDeliveryDate instanceof Date
      ? new Date(
          baselineDeliveryDate.getFullYear(),
          baselineDeliveryDate.getMonth(),
          baselineDeliveryDate.getDate()
        )
      : null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const earliestAllowedDate = addDays(today, CONFIG.leadDays);

    if (!(baseline instanceof Date) || !Number.isFinite(baseline.getTime())) {
      return {
        minDeliveryDate: earliestAllowedDate,
        maxDeliveryDate: addDays(today, CONFIG.maxDaysInFuture),
      };
    }

    const minDeliveryDate = earliestAllowedDate;
    const maxFromBaseline = addDays(baseline, CONFIG.maxDaysInFuture);
    const fallbackMax = addDays(minDeliveryDate, CONFIG.maxDaysInFuture);
    const candidateMax =
      maxFromBaseline instanceof Date && !Number.isNaN(maxFromBaseline.getTime())
        ? maxFromBaseline
        : fallbackMax;
    const maxDeliveryDate = isBeforeDay(candidateMax, minDeliveryDate)
      ? fallbackMax
      : candidateMax;

    return {
      minDeliveryDate,
      maxDeliveryDate,
    };
  }

  function isCalendarDateSelectable(context, dateValue) {
    if (!(dateValue instanceof Date)) return false;
    if (isBeforeDay(dateValue, context?.minDeliveryDate)) return false;
    if (context?.maxDeliveryDate instanceof Date && isAfterDay(dateValue, context.maxDeliveryDate)) return false;

    if (context?.availableDateSet instanceof Set) {
      const isoDateValue = formatDateInputValue(dateValue);
      return context.availableDateSet.has(isoDateValue);
    }

    return true;
  }

  function findNextSelectableDate(context, startDate) {
    const baselineDate = maxDate(startDate, context?.minDeliveryDate);
    if (!(baselineDate instanceof Date)) return null;

    if (!(context?.availableDateSet instanceof Set)) {
      return baselineDate;
    }

    let candidate = null;
    for (const isoDateValue of context.availableDateSet) {
      const parsed = parseDateValue(isoDateValue);
      if (!(parsed instanceof Date)) continue;
      if (isBeforeDay(parsed, baselineDate)) continue;
      if (!(candidate instanceof Date) || parsed.getTime() < candidate.getTime()) {
        candidate = parsed;
      }
    }

    return candidate;
  }

  function normalizeLocationId(rawValue) {
    const raw = String(rawValue || '').trim();
    if (!raw) return '';
    const match = raw.match(/(\d+)/g);
    if (!match || !match.length) return '';
    return String(match[match.length - 1] || '').trim();
  }

  function getMiddlewareApiBaseUrl() {
    const settingsUrl =
      window?.EveryTable?.api?.baseUrl || window?.etAffinityRescheduleConfig?.apiBaseUrl;
    const baseUrl = String(settingsUrl || '').trim();
    return baseUrl ? baseUrl.replace(/\/+$/, '') : '';
  }

  function createUnboundedAvailability() {
    return {
      // Null set means "do not restrict date selection by API availability".
      availableDateSet: null,
      availableDateDetailsByDate: new Map(),
    };
  }

  async function loadFulfillmentAvailability(locationId, maxDeliveryDate) {
    try {
      return await fetchFulfillmentAvailability(locationId, maxDeliveryDate);
    } catch (error) {
      // Keep the modal usable (delay and date selection) when availability metadata is unavailable.
      console.warn(
        '[affinity-reschedule] Falling back to unbounded calendar availability.',
        error
      );
      return createUnboundedAvailability();
    }
  }

  async function fetchFulfillmentAvailability(locationId, maxDeliveryDate) {
    const normalizedLocationId = normalizeLocationId(locationId);
    if (!normalizedLocationId) {
      throw new Error('LocationId is unavailable.');
    }
    const middlewareApiBaseUrl = getMiddlewareApiBaseUrl();
    if (!middlewareApiBaseUrl) {
      throw new Error('Middleware API base URL is unavailable.');
    }

    const response = await fetch(
      `${middlewareApiBaseUrl}/search/availability/location/${normalizedLocationId}`,
      {
        headers: {
          Accept: 'application/json',
        },
      }
    );
    if (!response.ok) {
      throw new Error('Unable to load location availability.');
    }

    const payload = await response.json();
    const pickupLocations = Array.isArray(payload?.pickupLocations)
      ? payload.pickupLocations
      : [];
    // Match by location id explicitly; payload order is not guaranteed.
    const matchingPickupLocation = pickupLocations.find((entry) => {
      const entryLocationId = normalizeLocationId(
        entry?.location_id || entry?.locationId || entry?.id || entry?.shopifyLocation?.id
      );
      return entryLocationId === normalizedLocationId;
    }) || null;
    const dateList = Array.isArray(matchingPickupLocation?.pickupDates)
      ? matchingPickupLocation.pickupDates
      : [];
    const availableDateSet = new Set();
    const availableDateDetailsByDate = new Map();

    for (const entry of dateList) {
      if (!entry || entry.isClosed) continue;
      const parsed = parseDateValue(entry.date);
      if (!(parsed instanceof Date)) continue;
      if (maxDeliveryDate instanceof Date && isAfterDay(parsed, maxDeliveryDate)) continue;
      const isoDateValue = formatDateInputValue(parsed);
      availableDateSet.add(isoDateValue);
      // Persist per-date windows so the second time-confirm modal can be date-aware.
      availableDateDetailsByDate.set(isoDateValue, {
        open: normalizeTimeInputValue(entry.open),
        close: normalizeTimeInputValue(entry.close),
      });
    }

    return {
      availableDateSet,
      availableDateDetailsByDate,
    };
  }

  function resolveTargetDeliveryDateForMode(options) {
    const mode = options?.mode || 'delay_1_week';
    const baselineChargeDate = options?.baselineChargeDate;
    const specificDeliveryDate = options?.specificDeliveryDate;

    if (mode === 'specific_delivery_date') {
      return specificDeliveryDate instanceof Date ? specificDeliveryDate : null;
    }

    if (!(baselineChargeDate instanceof Date)) {
      return null;
    }

    if (mode === 'delay_1_week') {
      return addDays(baselineChargeDate, 7 + CONFIG.leadDays);
    }

    return addDays(baselineChargeDate, CONFIG.leadDays);
  }

  function resolveTimeBoundsForDate(context, dateValue) {
    if (!(dateValue instanceof Date)) return null;
    const isoDateValue = formatDateInputValue(dateValue);
    if (!isoDateValue) return null;

    const dateDetailsByDate = context?.availableDateDetailsByDate;
    if (!(dateDetailsByDate instanceof Map)) return null;

    const dateDetails = dateDetailsByDate.get(isoDateValue);
    if (!dateDetails || typeof dateDetails !== 'object') return null;

    const closeTime = normalizeTimeInputValue(dateDetails.close);
    // Business rule: earliest delivery slot is 45 minutes after location open.
    const bufferedOpenTime = normalizeTimeInputValue(
      addMinutesToTime(dateDetails.open, 45)
    );
    const bufferedOpenParsed = parseTimeComponents(bufferedOpenTime);
    const closeParsed = parseTimeComponents(closeTime);
    let minTime = bufferedOpenTime;

    if (
      bufferedOpenParsed &&
      closeParsed &&
      bufferedOpenParsed.hours * 60 + bufferedOpenParsed.minutes >
        closeParsed.hours * 60 + closeParsed.minutes
    ) {
      // Degenerate same-point bound yields zero options after quarter-hour rounding.
      minTime = closeTime;
    }

    return {
      minTime,
      maxTime: closeTime,
    };
  }

  function formatDateInputValue(dateValue) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return '';
    }

    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDisplayDate(dateValue) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return 'Unknown date';
    }

    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(dateValue);
  }

  async function updateAddressFulfillmentDate(options) {
    const session = options?.session;
    const addressId = parseOptionalInteger(options?.addressId);
    const deliveryDate = options?.deliveryDate;
    const deliveryTime = normalizeQuarterHourTime(options?.deliveryTime);
    if (!session || !addressId || !(deliveryDate instanceof Date)) return;

    const maxAttempts = 4;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        await updateAddressFulfillmentDateOnce({
          session,
          addressId,
          deliveryDate,
          deliveryTime,
        });
        return;
      } catch (error) {
        if (!isConflictError(error) || attempt >= maxAttempts) {
          throw error;
        }
        // Recharge address writes can 409 immediately after subscription updates; retry with backoff.
        await sleep(250 * attempt);
      }
    }
  }

  async function updateAddressFulfillmentDateOnce(options) {
    const session = options?.session;
    const addressId = parseOptionalInteger(options?.addressId);
    const deliveryDate = options?.deliveryDate;
    const deliveryTime = normalizeQuarterHourTime(options?.deliveryTime);
    if (!session || !addressId || !(deliveryDate instanceof Date)) return;

    const addressResponse = await getAddress(session, addressId);
    const address = normalizeAddressResponse(addressResponse);
    if (!address || typeof address !== 'object') {
      throw new Error(`Unable to resolve address ${addressId} for fulfillment updates.`);
    }

    const existingAttributes = normalizeOrderAttributes(
      address.order_attributes ?? address.orderAttributes ?? address.attributes
    );
    const existingFulfillmentDateValue =
      readNamedAttributeValue(existingAttributes, 'Fulfillment Date') || '';
    const nextFulfillmentDateValue = buildFulfillmentDateValue(
      deliveryDate,
      existingFulfillmentDateValue,
      deliveryTime
    );
    const nextOrderAttributes = upsertNamedAttribute(
      existingAttributes,
      'Fulfillment Date',
      nextFulfillmentDateValue
    );

    await updateAddress(session, addressId, {
      order_attributes: nextOrderAttributes,
    });
  }

  function isConflictError(error) {
    const statusCandidates = [
      error?.status,
      error?.statusCode,
      error?.response?.status,
      error?.response?.statusCode,
      error?.data?.status,
      error?.data?.statusCode,
      error?.error?.status,
      error?.error?.statusCode,
    ];

    for (const candidate of statusCandidates) {
      const parsed = Number.parseInt(String(candidate), 10);
      if (parsed === 409) return true;
    }

    const message = String(error?.message || '');
    return /\b409\b/.test(message);
  }

  function sleep(milliseconds) {
    const duration = Number.parseInt(String(milliseconds), 10);
    const delay = Number.isFinite(duration) && duration > 0 ? duration : 0;
    return new Promise((resolve) => {
      window.setTimeout(resolve, delay);
    });
  }

  function normalizeAddressResponse(response) {
    if (!response) return null;
    if (response.address && typeof response.address === 'object') {
      return response.address;
    }
    if (typeof response === 'object') return response;
    return null;
  }

  function normalizeOrderAttributes(attributes) {
    if (!attributes) return [];

    if (Array.isArray(attributes)) {
      const output = [];
      for (const entry of attributes) {
        if (!entry) continue;
        if (Array.isArray(entry) && entry.length >= 2) {
          const name = String(entry[0] || '').trim();
          const value = String(entry[1] || '').trim();
          if (!name) continue;
          output.push({ name, value });
          continue;
        }
        if (typeof entry === 'object') {
          const name = String(entry.name ?? entry.key ?? '').trim();
          if (!name) continue;
          const value = String(entry.value ?? '').trim();
          output.push({ name, value });
        }
      }
      return output;
    }

    if (typeof attributes === 'object') {
      const output = [];
      for (const [key, rawValue] of Object.entries(attributes)) {
        const name = String(key || '').trim();
        if (!name) continue;
        output.push({ name, value: String(rawValue ?? '').trim() });
      }
      return output;
    }

    return [];
  }

  function readNamedAttributeValue(attributes, targetKey) {
    const normalizedTargetKey = normalizeAttributeKey(targetKey);
    if (!normalizedTargetKey || !Array.isArray(attributes)) return null;

    for (const entry of attributes) {
      if (!entry || typeof entry !== 'object') continue;
      const key = normalizeAttributeKey(entry.name ?? entry.key ?? '');
      if (key !== normalizedTargetKey) continue;
      return String(entry.value ?? '').trim() || null;
    }

    return null;
  }

  function upsertNamedAttribute(attributes, targetKey, targetValue) {
    const normalizedTargetKey = normalizeAttributeKey(targetKey);
    const output = Array.isArray(attributes)
      ? attributes
          .filter((entry) => entry && typeof entry === 'object')
          .map((entry) => ({
            name: String(entry.name ?? entry.key ?? '').trim(),
            value: String(entry.value ?? '').trim(),
          }))
          .filter((entry) => entry.name)
      : [];

    let didUpdate = false;
    for (const entry of output) {
      if (normalizeAttributeKey(entry.name) !== normalizedTargetKey) continue;
      entry.value = String(targetValue || '').trim();
      didUpdate = true;
      break;
    }

    if (!didUpdate) {
      output.push({
        name: String(targetKey || '').trim(),
        value: String(targetValue || '').trim(),
      });
    }

    return output;
  }

  function buildFulfillmentDateValue(deliveryDate, existingValue, deliveryTime) {
    const nextDatePart = formatDateInputValue(deliveryDate);
    if (!nextDatePart) return '';

    const existing = String(existingValue || '').trim();
    const normalizedTime = normalizeQuarterHourTime(deliveryTime);
    const existingTime = extractTimeInputValueFromFulfillmentDate(existing);
    // Prefer explicitly selected time; otherwise preserve existing time-of-day if present.
    const effectiveTime = normalizeTimeInputValue(normalizedTime || existingTime);
    if (effectiveTime) {
      return buildPacificDateTimeValue(nextDatePart, effectiveTime);
    }

    const match = existing.match(/^(\d{4}-\d{2}-\d{2})(.*)$/);
    if (match) {
      const suffix = String(match[2] || '');
      return `${nextDatePart}${suffix}`;
    }

    return nextDatePart;
  }

  function buildPacificDateTimeValue(datePart, normalizedTime) {
    const parsedDate = parseIsoDateParts(datePart);
    const parsedTime = parseTimeComponents(normalizedTime);
    if (!parsedDate || !parsedTime) {
      return `${datePart}T${normalizedTime}:00`;
    }

    const offsetMinutes = resolveTimeZoneOffsetMinutesForLocalDateTime(
      parsedDate,
      parsedTime,
      CONFIG.timeZone
    );
    const hourValue = String(parsedTime.hours).padStart(2, '0');
    const minuteValue = String(parsedTime.minutes).padStart(2, '0');

    return `${datePart}T${hourValue}:${minuteValue}:00${formatTimeZoneOffset(
      offsetMinutes
    )}`;
  }

  function parseIsoDateParts(datePart) {
    const match = String(datePart || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    return {
      year: Number.parseInt(match[1], 10),
      month: Number.parseInt(match[2], 10),
      day: Number.parseInt(match[3], 10),
    };
  }

  function resolveTimeZoneOffsetMinutesForLocalDateTime(dateParts, timeParts, timeZone) {
    const baseUtcMillis = Date.UTC(
      dateParts.year,
      dateParts.month - 1,
      dateParts.day,
      timeParts.hours,
      timeParts.minutes,
      0
    );
    let offsetMinutes = getTimeZoneOffsetMinutesAtInstant(baseUtcMillis, timeZone);

    // Iterate to converge local wall-clock offset near DST boundaries.
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const candidateUtcMillis = baseUtcMillis - offsetMinutes * 60 * 1000;
      const nextOffsetMinutes = getTimeZoneOffsetMinutesAtInstant(
        candidateUtcMillis,
        timeZone
      );
      if (nextOffsetMinutes === offsetMinutes) {
        return nextOffsetMinutes;
      }
      offsetMinutes = nextOffsetMinutes;
    }

    return offsetMinutes;
  }

  function getTimeZoneOffsetMinutesAtInstant(timestampMs, timeZone) {
    const instant = new Date(timestampMs);
    const shortOffset = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(instant)
      .find((part) => part.type === 'timeZoneName')?.value;

    const gmtOffsetMatch = String(shortOffset || '').match(
      /GMT([+-])(\d{1,2})(?::?(\d{2}))?/i
    );
    if (gmtOffsetMatch) {
      const sign = gmtOffsetMatch[1] === '-' ? -1 : 1;
      const hourOffset = Number.parseInt(gmtOffsetMatch[2], 10);
      const minuteOffset = Number.parseInt(gmtOffsetMatch[3] || '0', 10);
      return sign * (hourOffset * 60 + minuteOffset);
    }

    const shortName = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(instant)
      .find((part) => part.type === 'timeZoneName')?.value;

    if (/\bPDT\b/i.test(String(shortName || ''))) return -7 * 60;
    // Pacific fallback when shortOffset is unavailable in older environments.
    return -8 * 60;
  }

  function formatTimeZoneOffset(offsetMinutes) {
    const sign = offsetMinutes < 0 ? '-' : '+';
    const absoluteMinutes = Math.abs(offsetMinutes);
    const hourOffset = Math.floor(absoluteMinutes / 60);
    const minuteOffset = absoluteMinutes % 60;
    return `${sign}${String(hourOffset).padStart(2, '0')}:${String(
      minuteOffset
    ).padStart(2, '0')}`;
  }

  function normalizeAttributeKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function resolveRescheduleEventContext(event) {
    const detail = event?.detail || {};
    const payload = detail?.payload || {};
    // Affinity event payload keys vary by source action; gather known id shapes in one place.
    const rawSubscriptionIds = normalizeIntegerList(
      collectIntegerCandidates([
        payload.subscriptionId,
        payload.subscription_id,
        payload.subscriptionIds,
        payload.subscription_ids,
        payload.subscription?.id,
        payload.subscriptions,
        payload.items,
        detail.subscriptionId,
        detail.subscription_id,
        detail.subscriptionIds,
        detail.subscription_ids,
        detail.subscription?.id,
        detail.subscriptions,
        detail.items,
      ])
    );

    let subscriptionIds = rawSubscriptionIds.slice();
    let scope = 'single';
    if (subscriptionIds.length > 1) {
      // This customization intentionally scopes actions to the first subscription context.
      scope = 'multi-to-single';
      subscriptionIds = [subscriptionIds[0]];
    }

    return {
      rawSubscriptionIds,
      subscriptionIds,
      scope,
    };
  }

  function collectIntegerCandidates(values) {
    const output = [];
    const visitedObjects = new WeakSet();

    const collect = (value) => {
      if (value === null || typeof value === 'undefined') return;

      if (Array.isArray(value)) {
        for (const entry of value) {
          collect(entry);
        }
        return;
      }

      if (typeof value === 'object') {
        if (visitedObjects.has(value)) return;
        visitedObjects.add(value);
        // Recursively inspect nested object shapes without risking circular traversal.
        collect(value.id);
        collect(value.subscriptionId);
        collect(value.subscription_id);
        collect(value.subscriptionIds);
        collect(value.subscription_ids);
        return;
      }

      output.push(value);
    };

    collect(values);
    return output;
  }

  function normalizeIntegerList(values) {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    const output = [];
    for (const value of values) {
      const parsed = parseOptionalInteger(value);
      if (!parsed || seen.has(parsed)) continue;
      seen.add(parsed);
      output.push(parsed);
    }
    return output;
  }

  function parseOptionalInteger(rawValue) {
    const sharedParser = window.etRechargeUtils?.parseOptionalInteger;
    if (typeof sharedParser === 'function') {
      return sharedParser(rawValue);
    }
    if (rawValue === null || typeof rawValue === 'undefined') return null;
    const parsed = Number.parseInt(String(rawValue), 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  }

  function getSharedRechargeSdk() {
    const sharedGetter = window.etRechargeUtils?.getSharedRechargeSdk;
    if (typeof sharedGetter === 'function') {
      return sharedGetter();
    }
    const sharedSdk = window.etRechargeSdk;
    if (!sharedSdk || typeof sharedSdk.getMethod !== 'function') {
      throw new Error(
        'Shared Recharge SDK is unavailable. Ensure recharge-sdk-bootstrap.js loads before affinity-reschedule.js.'
      );
    }
    return sharedSdk;
  }

  function getRechargeMethod(pathSegments) {
    const sharedGetter = window.etRechargeUtils?.getRechargeMethod;
    if (typeof sharedGetter === 'function') {
      return sharedGetter(pathSegments);
    }
    return getSharedRechargeSdk().getMethod(pathSegments);
  }

  async function ensureSdkInitialized() {
    await getSharedRechargeSdk().ready();
  }

  function loginCustomerPortal(...args) {
    return getRechargeMethod(['auth', 'loginCustomerPortal'])(...args);
  }

  // Reads share one per-page-view memo with every other portal script (#341).
  function rechargeRead(pathSegments, session, ...args) {
    const sharedRead = window.etRechargeUtils?.memoizedRechargeRead;
    if (typeof sharedRead === 'function') {
      return sharedRead(pathSegments, session, args);
    }
    return getRechargeMethod(pathSegments)(session, ...args);
  }

  // Writes invalidate that memo before the caller's `await` resumes.
  function rechargeWrite(pathSegments, ...args) {
    const sharedWrite = window.etRechargeUtils?.runRechargeWrite;
    if (typeof sharedWrite === 'function') {
      return sharedWrite(pathSegments, ...args);
    }
    return getRechargeMethod(pathSegments)(...args);
  }

  function listSubscriptions(...args) {
    return rechargeRead(['subscription', 'listSubscriptions'], ...args);
  }

  function getAddress(...args) {
    return rechargeRead(['address', 'getAddress'], ...args);
  }

  function updateAddress(...args) {
    return rechargeWrite(['address', 'updateAddress'], ...args);
  }

  function updateSubscriptionChargeDate(...args) {
    return rechargeWrite(
      ['subscription', 'updateSubscriptionChargeDate'],
      ...args
    );
  }

  async function getPortalSession() {
    const sharedGetPortalSession = window.etRechargeUtils?.getPortalSession;
    if (typeof sharedGetPortalSession === 'function') {
      return sharedGetPortalSession();
    }
    if (state.sessionPromise) return state.sessionPromise;

    state.sessionPromise = (async () => {
      await ensureSdkInitialized();
      return loginCustomerPortal();
    })();

    try {
      return await state.sessionPromise;
    } catch (error) {
      state.sessionPromise = null;
      throw error;
    }
  }
})();
